<template>
  <div class="company-logo-wrap">
    <LoadingSpinner v-if="uploading" :variant="'solid'" :display-text="loadingSpinnerText" />
    <transition name="quickfade" appear mode="out-in">

      <!-- STAGE: GENERATOR -->
      <div v-if="stage === 'generator'" class="company-logo" key="step-generator" style="height: 100%; display: flex; flex-direction: column; justify-content: space-between;">
        <div class="intro-text">
          <p>
            <GkBadge 
              :text="$gettext('Premium')"
              :extra="(!currentCompany.is_premium && logoBotLoaded) ? $gettextInterpolate(basicCreditsLeftTxt, { credits: aiCreditsLeft }) : ''"
              icon="fa-solid fa-crown"
              type="premium"
              variant="base"
            />
          </p>
          {{ $gettext('The logo generator is a experimental feature and has a mind of it\'s own which can yield unexpected (but fun!) results!') }}
        </div>
        <div class="company-logo--sectionbox wizard-modal-onboarding-form">
          <ImageGenerator @generated="onImageGenerated" @loaded="onLogoBotLoaded" @cancel-generate="cancelGenerate()" />
        </div>
      </div>

      <!-- STAGE: EDITOR -->
      <ImageEditor
        v-if="stage === 'editor'"
        :image-src="unsavedB64image || selectedImageSrc"
        @save-edit="onEditedImage"
        :generated-image="isImageGenerated"
        @go-back="goToStage('main')"
        key="step-editor"
      />

      <!-- STAGE: GALLERY -->
      <div v-if="stage === 'gallery'" class="company-logo-gallery-wrap" key="step-gallery" style="display: flex; flex-direction: column;">
        <div>
          <GekkoButton 
            @click="goToStage('main')"
            :icon="'fa-solid fa-arrow-left'"
            :text="$gettext('Back')"
            variant="link text-blue"
            class="p-0 mb-2"
          />
        </div>
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
          <ImageGallery 
            :click-action="'select'"
            @edit-img="editFromGallery"
            @selected-img="onGalleryImageSelected"
          />
        </overlay-scrollbars>
      </div>

      <!-- STAGE: MAIN -->
      <div v-if="stage === 'main'" key="step-confirm" class="step-confirm-wrap">
        <!-- SELECTED IMAGE VIEW -->
        <div class="company-logo-confirm" key="company-logo-confirm" ref="component-container">
          <div class="selected-logo-confirm" :class="{ 'no-logo': !selectedImageSrc }">
            <div v-if="unsavedB64image || selectedImageSrc" class="selected-logo-confirm--logo" style="position: relative;">
              <img :src="unsavedB64image || selectedImageSrc">

              <!-- options menu (dropdown) -->
              <div v-if="selectedImageSrc" class="edit-dropdown gk-image-editor-controls--toolbar">
                <div class="toolbar-group">
                  <GekkoButton @click="removeLogo" icon="fa-solid fa-trash-can" />
                </div>
                <div class="toolbar-group">
                  <b-dropdown ref="options-dropdown" no-caret variant="ghost-blue" class="ui-dashboard-menu-dropdown wizard-dropdown gk-animated-dropdown">
                    <template #button-content>
                      <!-- <GekkoButton class="p-0" icon="fa-solid fa-ellipsis" /> -->
                      <font-awesome-icon icon="fa-solid fa-ellipsis" class="m-0" />
                    </template>
                    <div  class="dropdown-item" @click="stage = 'editor'"><font-awesome-icon :icon="'fa-solid fa-pen-ruler'" />{{ $gettext('Crop & Resize') }}</div>
                    <div class="dropdown-item" @click="stage = 'generator'"><font-awesome-icon :icon="'fa-solid fa-sparkles'" />{{ $gettext('Generate new with AI') }}</div>
                    <div v-if="images.length > 0" class="dropdown-item" @click="stage = 'gallery'"><font-awesome-icon :icon="'fa-solid fa-images'" />{{ $gettext('Select from gallery') }}</div>
                  </b-dropdown>
                </div>
              </div>
            </div>
            <div v-else class="w-100 text-center">
              <div v-if="!this.getMobileAppView">
                <div style="display: none;">
                  <ImageDropzone
                    :ref="'image-dropzone'"
                    :key="dropzoneKey"
                    :image_field_name="'logo'"
                    :options="{
                      autoProcessQueue: false,
                      url: Urls['company_profile_detail'](currentCompany.id),
                      delete_url: Urls['company_profile_delete_logo'](currentCompany.id),
                      clickable: ['#open-file-selector']
                    }"
                    @sending="uploading = true"
                    @uploaded="onImageUploaded"
                    @change="onImageUploaded"
                    @removed="removeLogo"
                    hide-preview hide-zone hide-actions
                  />
                </div>
                <div class="selected-logo-confirm--logo no-logo open-file-selector" id="open-file-selector">
                  <font-awesome-icon :icon="'fa-regular fa-image-slash'" />
                  {{ $gettext('Upload your company logo') }}
                </div>

                <div class="gk-divider">
                  <div class="divider-line"></div>
                  <div class="divider-text">{{ $gettext('or') }}</div>
                  <div class="divider-line"></div>
                </div>
              </div>


              <GekkoButton 
                @click="stage = 'generator'"
                :text="$gettext('Generate new AI logo')"
                variant="outline-primary"
                icon="fa-solid fa-sparkles"
                size="lg"
                class="magic-btn scaleup"
              />
            </div>

            <div v-if="showName && companyOrBrand && (unsavedB64image || selectedImageSrc)" class="selected-logo-confirm--name">{{ companyOrBrand.name }}</div>

          </div>
          <div v-if="wizard" class="w-100">
            <GekkoButton
              v-if="selectedImageSrc"
              @click="completeCompanyLogo"
              :text="$gettext('That\'s my logo')"
              variant="brand"
              size="xl"
              class="w-100"
            />
            <GekkoButton
              v-else
              @click="$emit('step-completed', true)"
              :text="$gettext('Don\'t need a logo')"
              variant="link"
              class="w-100"
            />
          </div>
        </div>
      </div>

    </transition>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import GekkoButton from "@/components/GekkoButton";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GkBadge from "@/components/GkBadge"

import ImageEditor from '@/components/logo_generator/ImageEditor'
import ImageGallery from '@/components/logo_generator/ImageGallery'
import ImageGenerator from '@/components/logo_generator/ImageGenerator'
import ImageDropzone from '@/components/logo_generator/ImageDropzone'

import AiAssistantService from '@/store/services/ai.assistant.service.js'

export default {
  name: 'CompanyLogo',
  components: {
    ImageEditor,
    ImageGallery,
    ImageGenerator,
    ImageDropzone,
    GekkoButton,
    LoadingSpinner,
    GkBadge
  },
  props: {
    wizard: {
      type: Boolean,
      default: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'company'
    }
  },
  data () {
    return {
      stage: 'main', // main, generator, editor, gallery // menu is deprecated
      uploading: false,
      dropzoneKey: 0,
      loadingSpinnerText: '',
      unsavedB64image: null,
      stabilityMaxPixelSize: 4000000,
      logoBotLimit: null,
      logoBotLoaded: false,
      loading: true,
      images: []
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      currentUser: "main/getCurrentUser",
      selectedBrand: "brands/getSelectedBrand",
      getMobileAppView: "main/getMobileAppView"
    }),
    companyOrBrand () {
      let companyOrBrand
      if (this.type === 'company') {
        companyOrBrand = this.currentCompany
      }
      if (this.type === 'brand') {
        companyOrBrand = this.selectedBrand
      }
      return companyOrBrand
    },
    selectedImageSrc () {
      let logo = null
      if (this.type === 'company' && this.currentCompany.logo) {
        logo = this.currentCompany.logo
      }
      if (this.type === 'brand' && this.selectedBrand && this.selectedBrand.brand_logo) {
        logo = this.selectedBrand.brand_logo
      }
      return logo
    },
    isImageGenerated () {
      return (this.unsavedB64image && true)
    },
    aiCreditsLeft () {
      return Math.max(0, this.logoBotLimit - this.currentUser.profile.ai_image_counter);
    },
    basicCreditsLeftTxt () {
      return this.$gettext('Trial: %{ credits } credits left')
    }
  },
  created () {
    this.setupData()
  },
  methods: {
    ...mapMutations({ 
      incrementAiCounter: 'main/SET_INCREMENT_CURRENT_USER_AI_COUNTER'
    }),
    ...mapActions({ 
      uploadCompanyLogo: 'main/uploadCompanyLogo',
      deleteCompanyLogo: 'main/deleteCompanyLogo',
      uploadBrandLogo: 'brands/uploadBrandLogo',
      deleteBrandLogo: 'brands/deleteBrandLogo'
    }),
    setupData () {
      this.loadingSpinnerText = this.$gettext('Saving image')
      if (this.getMobileAppView) {
        this.goToStage('generator')
      } else {
        this.goToStage('main')
      }
      AiAssistantService.getLogos().then((response) => {
        this.images = response.data
      })
    },
    onLogoBotLoaded (payload) {
      if (this.currentCompany.is_premium) {
        this.logoBotLimit = payload.premium
      } else {
        this.logoBotLimit = payload.basic
      }
      this.logoBotLoaded = true
    },
    editFromGallery (image_src) {
      // TODO option currently disabled
      this.stage = 'editor'
    },
    onImageGenerated (payload) {
      this.incrementAiCounter()
      if (window.mixpanel) {
        window.mixpanel.track("Wizard: logo - generated")
      }
      this.unsavedB64image = payload
      this.stage = 'editor'
    },
    onGalleryImageSelected (payload) {
      if (window.mixpanel) {
        window.mixpanel.track("Wizard: logo - gallery selected")
      }
      this.saveToCompany(payload.id, 'main')
    },
    onEditedImage (data) {
      if (window.mixpanel) {
        window.mixpanel.track("Wizard: logo - edited")
      }
      this.saveLogo(this.base64ToFile(data, 'logo_image.png', 'image/png'), 'main', true)
    },
    onImageUploaded (image) {
      if (window.mixpanel) {
        window.mixpanel.track("Wizard: logo - uploaded")
      }
      this.resizeImage(image).then((imageBlob) => {
        image = new File([imageBlob], 'logo_image.png', { type: imageBlob.type })
        this.saveLogo(image, 'main', true)
      })
    },
    async saveToCompany (image_id, next = 'main') {
      AiAssistantService.getLogoImageB64(image_id).then((response) => {
        this.saveLogo(this.base64ToFile(response.data, 'logo_image.png', 'image/png'), next, false)
      }).catch((error) => {
        console.log(error)
      })

    },
    /**
     * saveLogo will upload to company or brand and logos (if save_to_logos is true)
     * save_to_logos will be false when selecting from gallery
     */
    saveLogo (new_logo = null, next = 'editor', save_to_logos = true) {
      this.loadingSpinnerText = this.$gettext('Saving image')
      this.uploading = true
      if (this.type === 'company') {
        this.saveCompanyLogo(new_logo, next, save_to_logos)
      }
      if (this.type === 'brand') {
        this.saveBrandLogo(new_logo, next, save_to_logos)
      }
    },
    saveCompanyLogo (new_logo, next, save_to_logos) {
      // save company then to gallery if necessary
      const company_data = JSON.parse(JSON.stringify(this.currentCompany))
      company_data.logo = new_logo
      this.uploadCompanyLogo(company_data).then(() => {
        this.saveToLogoGallery(new_logo, next, save_to_logos)
      })
    },
    saveBrandLogo (new_logo, next, save_to_logos) {
      if (this.selectedBrand.id) {
        // existing brand, save brand then to gallery if necessary
        const brand_data = JSON.parse(JSON.stringify(this.selectedBrand))
        brand_data.brand_logo = new_logo
        this.uploadBrandLogo(brand_data).then(() => {
          this.saveToLogoGallery(new_logo, next, save_to_logos)
        })
      } else {
        // new brand, just emit and save to gallery if necessary
        this.fileToBase64(new_logo)
        this.$emit('attach-logo', new_logo)
        this.saveToLogoGallery(new_logo, next, save_to_logos, true)
      }
    },
    saveToLogoGallery (new_logo, next, save_to_logos, keep_unsaved = false) {
      this.closeMobileModal() // always try to close mobile modal after a save
      if (save_to_logos) {
        // save to logo gallery
        const new_logo_data = {
          name: 'logo_image.png',
          image: new_logo,
          user: this.currentUser.id
        }
        AiAssistantService.uploadNewLogo(new_logo_data).then((response) => {
          this.goToStage(next, keep_unsaved)
          this.uploading = false
        }).catch((error) => {
          console.log(error)
        })
      } else {
        this.goToStage(next, keep_unsaved)
        this.uploading = false
      }
    },
    cancelGenerate () {
      this.closeMobileModal()
      this.goToStage('main')
    },
    closeMobileModal () {
      if (this.getMobileAppView) {
        const currentQuery = { ...this.$route.query }
        currentQuery.logo_modal = 'close'
        this.$router.push({ query: currentQuery })
      }
    },
    dontNeedLogo () {
      if (this.type === 'brand' && !this.selectedBrand.id) {
        this.$emit('detach-logo', false)
      }
      this.goToStage('main')
    },
    removeLogo () {
      if (window.mixpanel) {
        window.mixpanel.track("Wizard: logo - removed")
      }
      this.loadingSpinnerText = this.$gettext('Removing logo')
      this.uploading = true
      if (this.type === 'company') {
        this.deleteCompanyLogo(this.currentCompany.id).then(() => {
          this.uploading = false
          this.goToStage('main')
        })
      }
      if (this.type === 'brand') {
        this.deleteBrandLogo(this.selectedBrand.id).then(() => {
        this.$emit('detach-logo', false)
          this.uploading = false
          this.goToStage('main')
        })
      }
    },
    goToStage (stage, keep_unsaved = false) {
      this.dropzoneKey++
      if (stage === 'main' && this.getMobileAppView) {
        this.stage = 'generator'
      } else {
        if (stage === 'main' && !keep_unsaved) {
          this.unsavedB64image = null
        }
        this.stage = stage
      }
    },
    base64ToFile (base64String, fileName, mimeType) {
      const binaryString = atob(base64String.split(',')[1])
      const len = binaryString.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: mimeType })
      return new File([blob], fileName, { type: mimeType })
    },
    async urlToFile (image_url) {
      const image_data = await fetch(image_url, { mode: 'no-cors' })
      const blob = await image_data.blob();
      const file = new File([blob], 'logo_image.png', { type: blob.type });
      return file
    },
    fileToBase64 (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        this.unsavedB64image = event.target.result
      }
      reader.readAsDataURL(file)
    },
    resizeImage (binaryData) {
      const blob = new Blob([binaryData], { type: 'image/png' })

      const img = new Image()

      const promise = new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          const scaleFactor = Math.sqrt(this.stabilityMaxPixelSize / (img.width * img.height))
          const newWidth = img.width * scaleFactor
          const newHeight = img.height * scaleFactor

          canvas.width = newWidth
          canvas.height = newHeight
          
          ctx.drawImage(img, 0, 0, newWidth, newHeight)
          
          canvas.toBlob((blob) => {
            resolve(blob)
          }, 'image/png')
        }

        img.onerror = reject
      })

      img.src = URL.createObjectURL(blob)

      return promise
    },
    /**
     * Final next step button
     */
    completeCompanyLogo () {
      this.$emit('step-completed', true)
    },
    upgradeToPremium () {
      window.location.href = '/subscriptions/manage'
    }
  }
}
</script>
<style lang="scss">
  .gk-image-uploader {
    display: flex;
    #dropzone-root .dropzone-preview-image img.dropzone-img {
      object-fit: cover;
    }
    &--uploader {
      width: 100%;
      max-width: 225px;
      aspect-ratio: 1/1;
      border-radius: $radiusLg;
      border: 1px solid #F4F4F4;
      overflow: hidden;
    }
    &--actions {
      width: 100%;
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        li {
          margin-bottom: 0.5rem;
          .btn {
            width: 100%;
            .svg-inline--fa {
              font-size: 20px;
              width: 24px;
              margin-right: 0.75rem;
            }
          }
        }
      }
    }
  }

.wizard-dropdown.gk-animated-dropdown {
  .dropdown-menu {
    padding: 0.5rem !important;
    box-shadow: rgba(11, 20, 26, 0.26) 0px 2px 5px 0px, rgba(11, 20, 26, 0.16) 0px 2px 10px 0px !important;
    transform: translate3d(-156px, -10px, 0) !important;
    &.show {
      transform: translate3d(-156px, 36px, 0) !important;
    }
    .dropdown-item {
      border-radius: calc(#{$gridBorderRadius} / 2);
      font-size: 16px;
      cursor: pointer;
      line-height: 1.5;
      padding: 0.5rem;
      .svg-inline--fa {
        margin-right: 0.5rem;
      }
      &:hover {
        background: rgba(34, 172, 138, 0.07);
        color: #06926f;
      }
    }
  }
}
.magic-btn {
  transition: all 0.3s ease !important;
  
  background: linear-gradient(60deg,#f79533,#f37055,#ef4e7b,#a166ab,#5073b8,#1098ad,#07b39b,#6fba82);
  -webkit-animation: animatedgradient 6s ease infinite alternate;
  animation: animatedgradient 6s ease infinite alternate;
  background-size: 300% 300%;
  color: white !important;
  border-color: white !important;
  .svg-inline--fa {
    margin-left: 0.5rem;
    color: white;
    border-color: white;
  }
  &:hover {
    color: white !important;
    border-color: white !important;
  }
  &.scaleup {
    &:hover {
      transform: scale(1.05);
    }
  }
  &.btn:disabled, &.btn.disabled {
    color: white !important;
    background: $colorBrand !important;
    animation: none;
    opacity: 1 !important;
  }
}
@keyframes animatedgradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
</style>
<style lang="scss" scoped>
  .company-logo-wrap {
    width: 100%;
    .intro-text {
      padding: 1rem;
      padding-top: 2rem;
      text-align: center;
      font-size: 14px;
      color: $colorTextSemi;
    }
    .company-logo {
      display: flex;
      flex-direction: column;
      height: 100%;
      &--section, &--sectionbox {
        p { 
          margin-bottom: 0.5rem;
          font-size: 1rem;
          color: $colorTextSemi;
        }
      }

      .ai-credits-counter {
        padding: 0 0.5rem !important;
        border-width: 1px !important;
      }
      
    }
    .step-confirm-wrap {
      display: flex;
      justify-content: center;
      height: 100%;
    }
    .company-logo-confirm {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      height: 100%; display: flex; flex-direction: column; justify-content: space-between;
      .selected-logo-confirm {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2rem 1rem;
        font-size: 1rem;
        width: 100%;
        // max-width: 400px;
        margin: 0 auto;

        margin-bottom: 1rem;

        flex: 1;
        justify-content: center;

        border: 1px solid $colorBrand;
        border-radius: $radiusLg;

        &.no-logo {
          border: 1px solid transparent;
        }

        .edit-dropdown {
          top: -0.25rem;
          right: -0.25rem;
          position: absolute;
          padding: 0;
          font-size: 14px;
        }
        &--name {
          font-family: DINNextLTPro-Medium, sans-serif;
          font-size: 22px;
          // white-space: nowrap;
          text-align: center;
          padding-top: 1.5rem;
          width: 100%;
        }
        &--logo {
          img {
            width: 100%;
            height: 100%;
            max-width: 200px;
            max-height: 200px;
            object-fit: contain;
            display: block;
          }
          &:hover {
            .gk-image-editor-controls--toolbar {
              opacity: 1;
            }
          }
          &.no-logo {
            border: 1px solid $colorBorderDash;
            border-radius: $radiusLg;
            transition: background-color 0.3s ease;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            width: 100%;
            height: 150px;
            background: transparent;
            color: #0d1216b3;
            text-align: center;
            .svg-inline--fa {
              font-size: 40px;
            }
            &:hover {
              background: $colorBgGrey;
            }
          }
        }
      }
    }
    .company-logo-gallery-wrap  {
      height: 100%;
    }
    .open-file-selector {
      cursor: pointer;
    }
  }
</style>